/**
 * wayai publish [--hub <id|name>] [-y]   (alias: wayai sync)
 *
 * Promote a preview hub to production:
 *   - first publish  → clones the preview into a NEW production hub
 *   - subsequent runs → syncs preview changes into the linked production hub
 *
 * Which one applies is auto-detected from the diff's `is_first_publish` flag —
 * there is no "wrong verb" to pick. Production serves live end users, so the
 * command shows the preview→production diff and confirms by default; `--yes`
 * (for CI) bypasses every prompt.
 *
 * publish/sync promote the SERVER-SIDE preview state, not local files. If the hub
 * is materialized locally with unpushed edits, we warn before proceeding — those
 * edits are not included until `wayai push`.
 */

import * as path from 'node:path';
import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { parseHubFolder } from '../lib/parser.js';
import { resolveHubTarget } from './pull.js';
import { confirm } from '../lib/utils.js';
import { findGitRoot, resolveWorkspaceDir } from '../lib/workspace.js';
import { warnLayoutOnce } from '../lib/layout.js';
import { printWarnings } from './push.js';
import { assertResourcePayloadFitsWithoutPruning, prepareResourcePayload } from '../lib/resource-files.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { assertInScope } from '../lib/worktree-scope.js';
import type { HubAsCodePayload } from '../lib/types.js';
import { HUB_DIFF_SECTIONS, describeSyncWarning, type DiffEntry, type HubDiffResult, type HubDiffSection } from '@wayai/core/contracts';

interface ParsedArgs {
  autoConfirm: boolean;
  hubSelector?: string;
}

export function parseArgs(args: string[]): ParsedArgs {
  let autoConfirm = false;
  let hubSelector: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--yes' || arg === '-y') {
      autoConfirm = true;
    } else if (arg === '--hub' && args[i + 1]) {
      hubSelector = args[++i];
    }
  }

  return { autoConfirm, hubSelector };
}

/** Symbol + ANSI color per diff status. */
const STATUS_STYLE: Record<DiffEntry['status'], { sym: string; color: number }> = {
  added: { sym: '+', color: 32 }, // green
  removed: { sym: '-', color: 31 }, // red
  updated: { sym: '~', color: 33 }, // yellow
};

function renderSection(title: string, entries: DiffEntry[]): void {
  if (entries.length === 0) return;
  console.log(`\n\x1b[1m${title}\x1b[0m`);
  for (const e of entries) {
    const { sym, color } = STATUS_STYLE[e.status];
    const type = e.type ? ` (${e.type})` : '';
    console.log(`\x1b[${color}m${sym} ${e.name}${type}\x1b[0m`);
  }
}

/** Each diff section's heading; a section missing here fails to compile. */
const SECTION_TITLES: Record<HubDiffSection, string> = {
  agents: 'Agents',
  agent_parameters: 'Agent parameters',
  connections: 'Connections',
  mcp_tools: 'MCP tools',
  tools: 'Tools',
  channels: 'Channels',
  states: 'States',
  teams: 'Teams',
  resources: 'Skills',
  resource_folders: 'Skill folders',
  resource_files: 'Skill files',
  agent_resources: 'Agent skills',
  message_templates: 'Message templates',
  contact_lists: 'Contact lists',
  automations: 'Automations',
  evals: 'Evals',
};

/**
 * Render a preview→production `HubDiffResult` to the terminal. Pure (only writes
 * to console) so it can be unit-tested directly, mirroring `push.ts`'s exported
 * `printSyncResult`. Walks `HUB_DIFF_SECTIONS`, the list the web publish modal walks;
 * a section an older server does not send reads as empty.
 */
export function renderHubDiff(result: HubDiffResult): void {
  if (result.hub_changed) {
    const { sym, color } = STATUS_STYLE[result.is_first_publish ? 'added' : 'updated'];
    console.log('\n\x1b[1mHub\x1b[0m');
    console.log(`\x1b[${color}m${sym} hub settings\x1b[0m`);
  }
  for (const section of HUB_DIFF_SECTIONS) renderSection(SECTION_TITLES[section], result[section] ?? []);
  console.log();
}

/** The subset of `ApiClient` `runPublish` needs — lets tests pass a fake. */
export type PublishClient = Pick<ApiClient, 'diff' | 'getHubDiff' | 'publishHub' | 'syncHub'>
  & Partial<Pick<ApiClient, 'pull'>>;

export type PublishOutcome = 'published' | 'synced' | 'up-to-date' | 'cancelled';

/**
 * Core publish/sync flow, decoupled from workspace/auth resolution so it can be
 * unit-tested with a fake client + injected confirm.
 */
export async function runPublish(
  client: PublishClient,
  opts: {
    hubId: string;
    /** Parsed local config when the hub is materialized locally; null otherwise. */
    localConfig: HubAsCodePayload | null;
    organizationId?: string;
    autoConfirm: boolean;
    confirmFn?: (question: string) => Promise<boolean>;
  },
): Promise<PublishOutcome> {
  const { hubId, localConfig, organizationId, autoConfirm } = opts;
  const confirmFn = opts.confirmFn ?? confirm;

  // Unpushed-changes guard (best-effort; only when materialized locally).
  // publish/sync promote the PREVIEW server state — local edits that were never
  // pushed are silently excluded, so warn before acting on stale preview state.
  if (localConfig) {
    let diffPayload = localConfig;
    if (client.pull) {
      try {
        diffPayload = prepareResourcePayload(localConfig, await client.pull(hubId, organizationId));
      } catch (err) {
        assertResourcePayloadFitsWithoutPruning(localConfig, err);
      }
    }
    const pushDiff = await client.diff(hubId, diffPayload, 'push', organizationId);
    if (pushDiff.has_changes) {
      console.log('\n\x1b[33mYou have local changes not yet pushed to this preview.\x1b[0m');
      console.log('Publishing promotes the PREVIEW (server) state — these local edits are NOT included.');
      console.log('Run `wayai push` first if you want them in production.');
      if (!autoConfirm) {
        const ok = await confirmFn('Continue with the current preview state anyway?');
        if (!ok) {
          console.log('Cancelled.');
          return 'cancelled';
        }
      }
    }
  }

  // Preview → production diff: the source of truth for what will change, and the
  // signal (is_first_publish) for which endpoint to call.
  const { data: diff } = await client.getHubDiff(hubId);

  if (!diff.has_changes && !diff.is_first_publish) {
    console.log('Production is already up to date. Nothing to sync.');
    return 'up-to-date';
  }

  console.log(
    diff.is_first_publish
      ? '\nPublish preview → new production hub:'
      : '\nSync preview → production:',
  );
  renderHubDiff(diff);

  if (!autoConfirm) {
    const ok = await confirmFn(
      diff.is_first_publish ? 'Publish to production?' : 'Sync these changes to production?',
    );
    if (!ok) {
      console.log('Cancelled.');
      return 'cancelled';
    }
  }

  if (diff.is_first_publish) {
    console.log('Publishing...');
    const { data } = await client.publishHub(hubId);
    console.log(`\nPublished. Production hub: ${data.production_hub_id}`);
    return 'published';
  }

  console.log('Syncing...');
  const { data } = await client.syncHub(hubId);
  console.log('\nSynced preview changes to production.');
  printWarnings((data.warnings ?? []).map(describeSyncWarning));
  return 'synced';
}

export async function publishCommand(args: string[]): Promise<void> {
  const { autoConfirm, hubSelector } = parseArgs(args);
  const repoConfig = requireRepoConfig();
  const { organization_id: organizationId } = repoConfig;

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const workspaceDir = resolveWorkspaceDir();
  const gitRoot = findGitRoot();
  if (gitRoot) warnLayoutOnce(gitRoot);

  // Resolve the preview hub the workspace operates on (same resolver as diff/
  // replicate: --hub | enclosing folder | sole preview; tolerates a UUID for a
  // hub not present locally).
  const { hubId, hubFolder } = resolveHubTarget(workspaceDir, hubSelector);

  let localConfig: HubAsCodePayload | null = null;
  if (hubFolder) {
    localConfig = parseHubFolder(hubFolder);
    // Refuse a read-only production mirror early (reached via cwd or --hub).
    // Defense-in-depth: the route + DO also block production writes.
    if (localConfig.hub_environment === 'production') {
      console.log(
        'This is a read-only production mirror — production hubs cannot be published. Publish the linked preview hub instead.',
      );
      return;
    }
  }

  // publish promotes to production — enforce the worktree scope on the resolved
  // hub whether or not it's materialized locally, matching `pull` (and stricter
  // than `push`, which only ever sees a local folder). A UUID-only `--hub`
  // target must not slip past the "outside this worktree's hubs" guard.
  assertInScope('hubs', hubId, hubFolder ? path.basename(hubFolder) : undefined);

  await runPublish(client, { hubId, localConfig, organizationId, autoConfirm });
}
